<template>
  <div id="kpt-detail-rmt-network">
    <vue-headful :title="`${remote.name} - Network`" />
    <div class="page-not-found" v-if="!$hasRole('Super User,Kymeta Admin,Product Support')">
      <h2 class="title has-text-grey-dark">
        <!-- user not authorized to see this page, show empty content -->
      </h2>
    </div>
    <div v-else>
      <div class="kpt-detail-content-actions">
        <div class="buttons" v-if="$hasRole('Super User,Kymeta Admin,Product Support')">
          <b-button type="is-primary is-outlined" size="is-small" icon-left="save" :loading="isSaving" v-if="isEditing" @click="saveNetworkConfig" :disabled="!hasUnsavedChanges || isSaving">Save</b-button>
          <b-button type="is-primary is-outlined" size="is-small" icon-left="times" v-if="isEditing" @click="cancelEdit" :disabled="isSaving">Cancel</b-button>
          <b-button type="is-primary is-outlined" icon-left="edit" size="is-small" @click="isEditing=true" v-if="!isEditing" :loading="isRemoteRefreshing" :disabled="isRemoteRefreshing">Edit</b-button>
        </div>
        <div id="kpt-detail-network-messages">
          <b-message type="is-warning" :class="{ 'is-hidden': !hasUnsavedChanges }" v-if="!!isEditing">
            You have unsaved changes, click "Save" to preserve your changes.
          </b-message>
          <b-message type="is-danger" has-icon v-if="errors.length">
            <b>Please correct the following error(s):</b>
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </b-message>
        </div>
      </div>
      <b-message type="is-info">Network address information is required for remotes subscribed to Evolution-based networks (Kymeta Broadband, Lepton).</b-message>
      <div id="kpt-detail-network-content">
        <header>
          <h6 class="is-uppercase"><strong>{{ isEditing ? 'Editing ' : ''}}Network Addresses</strong></h6>
        </header>
        <div v-if="!isEditing">
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>ETH0 IP Address</template>
            <template v-slot:value>{{remote.networkConfiguration.eth0IpAddress || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>ETH0 Subnet Mask</template>
            <template v-slot:value>{{remote.networkConfiguration.eth0SubnetMask || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>SAT0 IP Address</template>
            <template v-slot:value>{{remote.networkConfiguration.sat0IpAddress || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>SAT0 Subnet Mask</template>
            <template v-slot:value>{{remote.networkConfiguration.sat0SubnetMask || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>MGMT IP Address</template>
            <template v-slot:value>{{remote.networkConfiguration.mgmtIpAddress || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>MGMT Subnet Mask</template>
            <template v-slot:value>{{remote.networkConfiguration.mgmtSubnetMask || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>DHCP Gateway</template>
            <template v-slot:value>{{remote.networkConfiguration.dhcpGateway || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>DHCP Client IP Range Start</template>
            <template v-slot:value>{{remote.networkConfiguration.dhcpClientIpRangeStart || '-'}}</template>
          </detail-field>
          <detail-field v-if="remote.networkConfiguration">
            <template v-slot:label>DHCP Client IP Range End</template>
            <template v-slot:value>{{remote.networkConfiguration.dhcpClientIpRangeEnd || '-'}}</template>
          </detail-field>
        </div>
        <div v-else>
          <b-field label="ETH0 IP Address"
          :type="!validIpAddress(networkConfiguration.eth0IpAddress) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.eth0IpAddress) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.eth0IpAddress"
              type="text">
            </b-input>
          </b-field>
          <b-field label="ETH0 Subnet Mask"
          :type="!validIpAddress(networkConfiguration.eth0SubnetMask) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.eth0SubnetMask) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="Subnet Mask"
              v-model="networkConfiguration.eth0SubnetMask"
              type="text">
            </b-input>
          </b-field>
          <b-field label="SAT0 IP Address"
          :type="!validIpAddress(networkConfiguration.sat0IpAddress) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.sat0IpAddress) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.sat0IpAddress"
              type="text">
            </b-input>
          </b-field>
          <b-field label="SAT0 Subnet Mask"
          :type="!validIpAddress(networkConfiguration.sat0SubnetMask) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.sat0SubnetMask) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.sat0SubnetMask"
              type="text">
            </b-input>
          </b-field>
          <b-field label="MGMT IP Address"
          :type="!validIpAddress(networkConfiguration.mgmtIpAddress) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.mgmtIpAddress) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.mgmtIpAddress"
              type="text">
            </b-input>
          </b-field>
          <b-field label="MGMT Subnet Mask"
          :type="!validIpAddress(networkConfiguration.mgmtSubnetMask) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.mgmtSubnetMask) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.mgmtSubnetMask"
              type="text">
            </b-input>
          </b-field>          
           <b-button type="is-primary is-outlined" size="is-small" @click="getDHCPCalculatedField">Auto Calculate DHCP values</b-button>
          <b-field label="DHCP Gateway"
          :type="!validIpAddress(networkConfiguration.dhcpGateway) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.dhcpGateway) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.dhcpGateway"
              type="text">
            </b-input>
          </b-field>
          <b-field label="DHCP Client IP Range: Start"
          :type="!validIpAddress(networkConfiguration.dhcpClientIpRangeStart) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.dhcpClientIpRangeStart) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.dhcpClientIpRangeStart" 
              type="text">
            </b-input>
          </b-field>
          <b-field label="DHCP Client IP Range: End"
          :type="!validIpAddress(networkConfiguration.dhcpClientIpRangeEnd) ? 'is-danger' : ''"
          :message="!validIpAddress(networkConfiguration.dhcpClientIpRangeEnd) ? 'Please enter a valid IP address.' : ''">
            <b-input placeholder="IP Address"
              v-model="networkConfiguration.dhcpClientIpRangeEnd"
              type="text">
            </b-input>
          </b-field>
        </div>
      </div>
      <div v-if="networkInventoryVisible">
        <hr class="mb-1"/>
        <header>
          <h6 class="is-uppercase mb-5"><strong>Network Inventory</strong></h6>
        </header>
        <div class="buttons" v-if="$hasRole('Super User,Kymeta Admin,Product Support')">
            <b-tooltip label="Keep atleast one beam in Network Inventory from earlier provisioned beams." :active="!!isLeastOneProvisionedUnChecked" position="is-right" type="is-warning">
              <b-button type="is-primary is-outlined" size="is-small" icon-left="save" v-if="isRowCheckable" @click="saveNetworkRemoteMappings" :disabled="!!isLeastOneProvisionedUnChecked">Save</b-button>
            </b-tooltip>
            <b-button type="is-primary is-outlined ml-1" size="is-small" icon-left="times" v-if="isRowCheckable"  @click="isRowCheckable = false" >Cancel</b-button>
            <b-tooltip label="Sync in progress with NMS." :active="!!syncInProgress" position="is-right" type="is-warning">
              <b-button type="is-primary is-outlined" icon-left="edit" size="is-small" @click="isRowCheckable = true" v-if="!isRowCheckable" :disabled="!!syncInProgress || networkInventoryData.length == 0">Edit</b-button>
            </b-tooltip>
        </div>
        <b-message type="is-info">
          This section defines the Evolution networks this remote is allowed to associate with. Making changes here will not update the NMS; resync will be required on the 
          <router-link v-if="$hasRole('Super User,Kymeta Admin,Product Support')" :to="{ name: 'remoteConfiguration' }"><a>Configuration</a></router-link>
          <span v-else> Configuration</span> page.
        </b-message>
        <b-table 
          :data="networkInventoryData"
          :hoverable="true"
          :loading="isLoadingNwData"
          class="is-full-height is-full-width overflow-y-inherit">
          <b-table-column v-slot="props" field="networkName" label="Internal Name">
            {{ props.row.networkName }}
          </b-table-column>
          <b-table-column v-slot="props" field="displayName" label="Name">
            {{ props.row.displayName }}
          </b-table-column>
          <b-table-column v-slot="props" field="networkId" label="Beam Id" centered>
            {{ props.row.networkId }}
          </b-table-column>
          <b-table-column v-slot="props" field="isRequired" label="Desired Network" type="checkbox" centered>
            <b-checkbox v-model="props.row.isRequired" :disabled="!isRowCheckable"></b-checkbox>
          </b-table-column>
          <b-table-column v-slot="props" field="template" label="Template" centered>
            <b-button type="is-primary is-outlined" size="is-small" @click="viewTemplate(props.row.displayName)" :loading="isSaving" >View</b-button>
          </b-table-column>
          <template slot="empty">
            <p class="pb-3">No Network Inventory found.</p>
            <b-message class="is-warning req-conditions-msg">
              <p>
                Please note, to see available Beams, the Remote must have the following conditions set:
                <ul>
                  <li>Active Subscription</li>
                  <li>Modem Model</li>
                  <li>Modem Serial</li>
                  <li>Antenna Model</li>
                </ul>
              </p>
            </b-message>
          </template>
        </b-table>
        <template v-if="template">
          <hr/>
          <b-field label="Template">
                <b-input
                  type="textarea"
                  rows="20"
                  v-model="template" 
                  readonly/>
          </b-field>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { orderBy } from "lodash";

export default {
  data() {
    return {
      // eslint-disable-next-line no-useless-escape
      ipAddressRegexPattern: '^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$',
      isEditing: false,
      isSaving: false,
      errors: [],
      networkConfiguration: {
        eth0IpAddress: null,
        eth0SubnetMask: null,
        sat0IpAddress: null,
        sat0SubnetMask: null,
        mgmtIpAddress: null,
        mgmtSubnetMask: null,
        dhcpGateway: null,
        dhcpClientIpRangeStart: null,
        dhcpClientIpRangeEnd: null
      },
      networkNmsTypes: [],
      nmsId: null,
      nmsType: null,
      antennaType: null,
      modemType: null,
      template: null,
      isLoadingNwData: false,
      isRowCheckable: false,
      networkRemoteMappings: [], 
      networkInventoryVisible: false
    }    
  },
  async mounted() { 
    if (this.remote) this.networkConfiguration = { ...this.remote.networkConfiguration };
    // get network inventory visible from gv
    this.networkInventoryVisible = await this.$store.dispatch('fetchNetworkInventoryVisible');
    if (this.networkInventoryVisible) {
      this.isLoadingNwData = true;
      // get network NMS types
      await this.$store.dispatch('fetchNms');
      const networkNmsTypes = this.$store.state.admin?.network?.nmsTypes;
      if (!networkNmsTypes || networkNmsTypes?.length == 0) {
        this.isLoadingNwData = false; 
        return [];
      }
      if (this.activeOrFutureSubscription) {
        let nms = null;
        nms = this.hasTransecSub 
          ? networkNmsTypes?.filter(n => n.name.toLowerCase().includes('transec'))
          : nms = networkNmsTypes?.filter(n => n.name.toLowerCase().includes('kbb'));

        if (!!nms && nms?.length > 0) {
          const isU7 = this.remote?.devices?.antenna1Model?.toLowerCase()?.includes('u7');
          const isU8 = this.remote?.devices?.antenna1Model?.toLowerCase()?.includes('u8');
          this.nmsId = nms.map(n => n.id)[0];
          this.nmsType = nms.map(n => n.type)[0];
          // eslint-disable-next-line no-nested-ternary
          this.antennaType = isU8 ? 'u8' : isU7 ? 'u7' : null;
          this.modemType = this.remote?.devices?.modemModel;
        }
      }
      // get network remote mapping by id
      this.networkRemoteMappings = await this.$store.dispatch("fetchNetworkRemoteMappings", { remoteId: this.remote.id });
    }
    this.isLoadingNwData = false; 
    return [];
  },
  computed: {
    remote() {
      return this.$store.state.remotes.remote;
    },
    isRemoteRefreshing() {
      return this.$store.state.remotes.remoteRefreshing;
    },
    
    hasUnsavedChanges() {
      // simplify variable reference
      const remoteNetworkConfig = this.remote.networkConfiguration;
      const componentNetworkConfig = this.networkConfiguration;

      // check for any differences
      if (remoteNetworkConfig.eth0IpAddress != componentNetworkConfig.eth0IpAddress) return true;
      if (remoteNetworkConfig.eth0SubnetMask != componentNetworkConfig.eth0SubnetMask) return true;
      if (remoteNetworkConfig.sat0IpAddress != componentNetworkConfig.sat0IpAddress) return true;
      if (remoteNetworkConfig.sat0SubnetMask != componentNetworkConfig.sat0SubnetMask) return true;
      if (remoteNetworkConfig.mgmtIpAddress != componentNetworkConfig.mgmtIpAddress) return true;
      if (remoteNetworkConfig.mgmtSubnetMask != componentNetworkConfig.mgmtSubnetMask) return true;
      if (remoteNetworkConfig.dhcpGateway != componentNetworkConfig.dhcpGateway) return true;
      if (remoteNetworkConfig.dhcpClientIpRangeStart != componentNetworkConfig.dhcpClientIpRangeStart) return true;
      if (remoteNetworkConfig.dhcpClientIpRangeEnd != componentNetworkConfig.dhcpClientIpRangeEnd) return true;

      // no differences, nothing to save
      return false;
    },
    networkInventoryData() {
      const kymetaBeamsList = this.$store.state.remotes?.remote?.kymetaBeams;
      if (!!kymetaBeamsList && kymetaBeamsList?.length > 0) {
        return orderBy(kymetaBeamsList, ['networkName'], ['asc']); 
      }
      return [];
    },
    onNmsBeams() {
      const onNms = this.$store.state.remotes?.remote?.beams
      if (!!onNms && onNms?.length > 0) {
        return orderBy(onNms, ['name'], ['asc']); 
      }
      return [];
    },
    activeSubscription() { return this.$store.getters.activeSubscription; },
    futureSubscriptions() { return this.$store.getters.futureSubscriptions; },
    activeOrFutureSubscription() {
      if (this.activeSubscription) return this.activeSubscription;
      if (this.futureSubscriptions) return orderBy(this.futureSubscriptions, ['startDate'], ['asc'])[0];
      return null;
    },
    hasTransecSub() {
      return this.activeOrFutureSubscription && this.activeOrFutureSubscription.productShortcode.includes('PR_KB_TRANSEC');
    },
    syncInProgress() {
      return !!this.remote.syncInProgress;
    },
    isLeastOneProvisionedUnChecked() {
      if (this.onNmsBeams && this.networkInventoryData) {
        const networkInvenotryNetworkIds = this.networkInventoryData.filter(n => !!n.isRequired).map(network => network.networkId);
        const onNmsNetworkIds = this.onNmsBeams.filter(n => !!n?.provisioned).map(network => network.networkId);
        if (!!networkInvenotryNetworkIds && !!onNmsNetworkIds && !!onNmsNetworkIds?.length > 0) return !(onNmsNetworkIds.filter(id => networkInvenotryNetworkIds.includes(id))?.length >= 1);
      }
      return false;
    }
  },
  methods: {
    validateDHCPInputs() {
      this.errors = [];
      const netConfig = this.networkConfiguration;
      if ((netConfig.eth0IpAddress && netConfig.eth0IpAddress.trim() != "") && (netConfig.eth0SubnetMask && netConfig.eth0SubnetMask.trim() != "")) {
        if (netConfig.eth0IpAddress && !this.validIpAddress(netConfig.eth0IpAddress)) this.errors.push(`Please enter a valid IP Address for 'ETH0 IP'.`);
        if (netConfig.eth0SubnetMask && !this.validIpAddress(netConfig.eth0SubnetMask)) this.errors.push(`Please enter a valid IP Address for 'ETH0 Subnet Mask'.`); 
      } else {
        this.errors.push(`Please enter IP Addresses for 'ETH0 IP' and 'ETH0 SubnetMask'.`);
      }
      return this.errors.length == 0;
    },
    cancelEdit() {
      // reset to original values
      if (this.remote) this.networkConfiguration = { ...this.remote.networkConfiguration };
      this.errors = [];
      this.isEditing = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'cancelRemoteEdit', 'Cancelled remote edit.', { remoteId: this.remote.id });
    },
    validateIpAddresses() {
      this.errors = [];
      const netConfig = this.networkConfiguration;
      if (netConfig.eth0IpAddress && !this.validIpAddress(netConfig.eth0IpAddress)) this.errors.push(`Please enter a valid IP Address for 'ETH0 IP'.`);
      if (netConfig.eth0SubnetMask && !this.validIpAddress(netConfig.eth0SubnetMask)) this.errors.push(`Please enter a valid IP Address for 'ETH0 Subnet Mask'.`);
      if (netConfig.sat0IpAddress && !this.validIpAddress(netConfig.sat0IpAddress)) this.errors.push(`Please enter a valid IP Address for 'SAT0 IP'.`);
      if (netConfig.sat0SubnetMask && !this.validIpAddress(netConfig.sat0SubnetMask)) this.errors.push(`Please enter a valid IP Address for 'SAT0 Subnet Mask'.`);
      if (netConfig.mgmtIpAddress && !this.validIpAddress(netConfig.mgmtIpAddress)) this.errors.push(`Please enter a valid IP Address for 'MGMT IP'.`);
      if (netConfig.mgmtSubnetMask && !this.validIpAddress(netConfig.mgmtSubnetMask)) this.errors.push(`Please enter a valid IP Address for 'MGMT Subnet Mask'.`);
      if (netConfig.dhcpGateway && !this.validIpAddress(netConfig.dhcpGateway)) this.errors.push(`Please enter a valid IP Address for 'DHCP Gateway'.`);
      if (netConfig.dhcpClientIpRangeStart && !this.validIpAddress(netConfig.dhcpClientIpRangeStart)) this.errors.push(`Please enter a valid IP Address for 'DCHP Range Start'.`);
      if (netConfig.dhcpClientIpRangeEnd && !this.validIpAddress(netConfig.dhcpClientIpRangeEnd)) this.errors.push(`Please enter a valid IP Address for 'DHCP Range End'.`);
      
      return this.errors.length == 0;
    },
    validIpAddress(ipAddress) {
      const re = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
      return re.test(ipAddress);
    },
    async saveNetworkConfig() { 
      // simplify variable reference, for validation
      const componentNetworkConfig = this.networkConfiguration;

      // all fields blank we will allow to save, regardless of any DHCP calculated values. [ALL FIELDS BLANK]
      if (!componentNetworkConfig?.eth0IpAddress && !componentNetworkConfig?.eth0SubnetMask && !componentNetworkConfig?.sat0IpAddress && !componentNetworkConfig?.sat0SubnetMask && !componentNetworkConfig?.mgmtIpAddress && !componentNetworkConfig?.mgmtSubnetMask && !componentNetworkConfig?.dhcpGateway && !componentNetworkConfig?.dhcpClientIpRangeStart && !componentNetworkConfig?.dhcpClientIpRangeEnd) {
        // if all fileds blank, do nothing and continue to save
      } else {
        // validations on few fields
        // if eth0ip address, or eth0subnetmark not entered, but any other, or even dhcp
        if ((!componentNetworkConfig?.eth0IpAddress || !componentNetworkConfig?.eth0SubnetMask)
            && (!!componentNetworkConfig?.sat0IpAddress || !!componentNetworkConfig?.sat0SubnetMask || !!componentNetworkConfig?.mgmtIpAddress || !!componentNetworkConfig?.mgmtSubnetMask || !!componentNetworkConfig?.dhcpGateway || !!componentNetworkConfig?.dhcpClientIpRangeStart || !!componentNetworkConfig?.dhcpClientIpRangeEnd)) {
          this.$buefy.toast.open({
            duration: 5000,
            type: 'is-danger',
            message: `Please enter valid values for ETH0IpAddress or ETH0Subnetmask.`
          });
          return null;
        }
        const componentNetworkConfigDhcpGateway = componentNetworkConfig.dhcpGateway;
        await this.getDHCPCalculatedField();
        const dhcpGatewayLatest = this.networkConfiguration?.dhcpGateway;
        // if eth0ip address, eth0subnetmark, and dhcpgateway entered, but dhcp gateway is not matching with latest entered values
        if ((!!componentNetworkConfig?.eth0IpAddress && !!componentNetworkConfig?.eth0SubnetMask) && (componentNetworkConfigDhcpGateway != dhcpGatewayLatest)) {
          this.$buefy.toast.open({
            duration: 5000,
            type: 'is-danger',
            message: `DHCP Gateway value should be ${dhcpGatewayLatest}.`
          });
          return null;
        }
      }
      this.isSaving = true;
      const isValidated = this.validateIpAddresses();
      if (!isValidated) {
        this.isSaving = false;
        this.$buefy.toast.open({
          duration: 5000,
          type: 'is-danger',
          message: 'Please enter valid values.'
        });
        return null;
      }

      // assign the RemoteId for proper context
      this.networkConfiguration.remoteId = this.remote.id;
      const saveResult = await this.$store.dispatch('updateRemoteNetworkConfiguration', this.networkConfiguration);
      this.isSaving = false;
      // catch any errors
      if (!saveResult.isSuccess) {
        this.$buefy.toast.open({
          duration: 5000,
          type: 'is-danger',
          message: `Unable to save, Error : ${saveResult?.data}`
        });
        return null;
      }

      // get latest values from vuex state
      this.networkConfiguration = JSON.parse(JSON.stringify(this.remote.networkConfiguration));
      this.isEditing = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'saveRemoteNetworkConfig', 'Save network config clicked.', { remoteId: this.remote.id });
      return true;
    },
    async viewTemplate(beamName) {
      this.isLoadingNwData = true;
      this.template = null;
      if (this.nmsType && this.antennaType && this.modemType && beamName) {
        const network = {
          nmsType: this.nmsType,
          antennaType: this.antennaType,
          modemType: this.modemType,
          beamDisplayName: beamName
        };
        const loadTemplate = await this.$store.dispatch("getTemplate", network);
        if (!loadTemplate.isSuccess) {
          if (loadTemplate.error != "The specified template does not exist.") {
            this.$buefy.toast.open({
              duration: 3000,
              message: loadTemplate.error || 'Error encountered while attempting to load Template.',
              type: 'is-danger'
            });
          }
          this.template = loadTemplate.error;
        } else this.template = loadTemplate.data;
      }
      this.isLoadingNwData = false;
    },
    async saveNetworkRemoteMappings() {
      this.isLoadingNwData = true;
      if (this.networkInventoryData) {
        const payload = {
          nmsId: this.nmsId,
          remoteId: this.remote.id,
          networkIds: this.networkInventoryData.filter(n => n.isRequired).map(network => network.networkId)
        }
        const addResult = await this.$store.dispatch('addNetworkRemoteMapping', payload);
        if (!addResult) {
          this.$buefy.toast.open({
            duration: 3000,
            type: 'is-danger',
            message: 'Unable to save networks.'
          });
          return;
        }
        this.$buefy.toast.open({
          duration: 3000,
          type: 'is-success',
          message: 'Networks saved successfully.'
        });
      }
      // get network remote networks by id
      this.networkRemoteMappings = await this.$store.dispatch("fetchNetworkRemoteMappings", { remoteId: this.remote.id });
      this.isRowCheckable = false;
      this.isLoadingNwData = false;
    },
    // eslint-disable-next-line consistent-return
    async getDHCPCalculatedField() {
      const isValidated = this.validateDHCPInputs();
      if (!isValidated) {
        this.$buefy.toast.open({
          duration: 2000,
          type: 'is-danger',
          message: 'Please enter valid values.'
        });
      }
      const networkConfig = this.networkConfiguration;
      const payload = {
        eth0IPAddress: networkConfig.eth0IpAddress,
        eth0SubnetMask: networkConfig.eth0SubnetMask,
        remoteId: this.remote.id
      }
      const calculatedFieldsResponse = await this.$store.dispatch('getDHCPCalculatedField', payload);   
      // catch any errors
      if (!calculatedFieldsResponse.isSuccess) {
        this.$buefy.toast.open({
          duration: 5000,
          type: 'is-danger',
          message: `Unable to calulate DHCP Fields, Error : ${calculatedFieldsResponse?.data}`
        });
        return null;
      }
      this.networkConfiguration = { 
        ...this.networkConfiguration, 
        dhcpClientIpRangeStart: calculatedFieldsResponse.data.dhcpClientIPRangeStart,
        dhcpClientIpRangeEnd: calculatedFieldsResponse.data.dhcpClientIPRangeEnd,
        dhcpGateway: calculatedFieldsResponse.data.dhcpGateway
      };
    }
  },
  watch: {
    // eslint-disable-next-line quote-props
    'isRowCheckable': function (newValue, oldValue) {
      this.isLoadingNwData = true;
      if (newValue == false && oldValue == true) {
        if (this.networkRemoteMappings && this.networkInventoryData) {
          this.networkInventoryData?.forEach(nid => {
            // eslint-disable-next-line no-param-reassign
            nid.isRequired = (this.networkRemoteMappings?.filter(n => n?.networkId == nid.networkId)?.length > 0)
          });
        }
      }
      this.isLoadingNwData = false;
    }
  }
}
</script>
<style lang="scss" scoped>
  #kpt-detail-rmt-network {
    height: 100%;
    padding: 12px;

    .kpt-detail-content-actions {
      .buttons {
        margin-bottom: -8px;
      }
      #kpt-detail-network-messages {
        margin-top: 12px;

        .message:not(:last-child) {
          margin-bottom: 12px;
        }
      }
    }
    
    .page-not-found {
      height: inherit;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    #kpt-detail-network-content {
      width: 350px;

      &::v-deep .kpt-detail-field-label {
        width: 200px !important;
      }
    }

    ::v-deep .table-wrapper {
      overflow: inherit;
    }

    .req-conditions-msg {
      padding-top: 6px;

      ul {
        padding-top: 6px;
        list-style: disc;
        margin-left: 22px;
      }
    }
  }
</style>
